import * as React from "react";
import clsx from "clsx";
import { animated, useSpring } from "@react-spring/web";
import { styled } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import ArticleIcon from "@mui/icons-material/Article";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FolderRounded from "@mui/icons-material/FolderRounded";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { treeItemClasses } from "@mui/x-tree-view/TreeItem";
import {
  unstable_useTreeItem2 as useTreeItem2,
  UseTreeItem2Parameters,
} from "@mui/x-tree-view/useTreeItem2";
import {
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Root,
} from "@mui/x-tree-view/TreeItem2";
import { TreeItem2Icon } from "@mui/x-tree-view/TreeItem2Icon";
import { TreeItem2Provider } from "@mui/x-tree-view/TreeItem2Provider";
import { TreeItem2DragAndDropOverlay } from "@mui/x-tree-view/TreeItem2DragAndDropOverlay";
import { ProjectFile, ProjectFileType, SortDirection } from "generated/graphql";
import { ProjectFilesContext } from "modules/proposals/providers/ProjectFilesContextProvider";
import { Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useTreeViewApiRef } from "@mui/x-tree-view";
import AddFileOrFolder from "./AddFileOrFolder";
import useFileSearch from "hooks/projectFiles/useFileSearch";
import useSortProjectFile from "hooks/projectFiles/useSortProjectFile";
import SearchAndSortFiles from "./SearchAndSortFiles";
import { validateFileSelection } from "helpers/validateFileSelection";

type FileType =
  | "image"
  | "pdf"
  | "doc"
  | "video"
  | "pinned"
  | "trash"
  | ProjectFileType.FOLDER
  | ProjectFileType.FILE;

function DotIcon() {
  return (
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "70%",
        bgcolor: "warning.main",
        display: "inline-block",
        verticalAlign: "middle",
        zIndex: 1,
        mx: 1,
      }}
    />
  );
}
declare module "react" {
  interface CSSProperties {
    "--tree-view-color"?: string;
    "--tree-view-bg-color"?: string;
  }
}

const StyledTreeItemRoot = styled(TreeItem2Root)(({ theme }) => ({
  color: theme.palette.mode === "light" ? theme.palette.grey[800] : theme.palette.grey[400],
  position: "relative",
  paddingLeft: theme.spacing(2),
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: theme.spacing(3.5),
  },
})) as unknown as typeof TreeItem2Root;

const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
  flexDirection: "row-reverse",
  borderRadius: theme.spacing(0.7),
  marginBottom: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
  padding: "5",
  paddingRight: theme.spacing(1),
  fontWeight: 500,
  [`&.Mui-expanded `]: {
    "&:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon": {
      color: theme.palette.mode === "light" ? "#aaa" : theme.palette.primary.dark,
    },
    "&::before": {
      content: '""',
      display: "block",
      position: "absolute",
      left: "16px",
      top: "44px",
      height: "calc(100% - 48px)",
      width: "1.5px",
      backgroundColor:
        theme.palette.mode === "light" ? theme.palette.grey[300] : theme.palette.grey[700],
    },
  },
  "&:hover": {
    backgroundColor: "#e5e5e5",
    color: "#000",
  },
  [`&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused`]: {
    backgroundColor: "#556",
    color: theme.palette.primary.contrastText,
  },
}));

const AnimatedCollapse = animated(Collapse);

function TransitionComponent(props: TransitionProps) {
  const style = useSpring({
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(0,${props.in ? 0 : 20}px,0)`,
    },
  });

  return <AnimatedCollapse style={style} {...props} />;
}

const StyledTreeItemLabelText = styled(Typography)({
  color: "inherit",
  fontWeight: 400,
}) as unknown as typeof Typography;

interface CustomLabelProps {
  children: React.ReactNode;
  icon?: React.ElementType;
  expandable?: boolean;
}

function CustomLabel({ icon: Icon, expandable, children, ...other }: CustomLabelProps) {
  return (
    <TreeItem2Label
      {...other}
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {Icon && (
        <Box
          component={Icon}
          className="labelIcon"
          color="inherit"
          sx={{ mr: 1, fontSize: "1.2rem" }}
        />
      )}

      <StyledTreeItemLabelText variant="body2">{children}</StyledTreeItemLabelText>
      {expandable && <DotIcon />}
    </TreeItem2Label>
  );
}

const isExpandable = (reactChildren: React.ReactNode) => {
  if (Array.isArray(reactChildren)) {
    return reactChildren.length > 0 && reactChildren.some(isExpandable);
  }
  return Boolean(reactChildren);
};

const getIconFromFileType = (fileType: FileType) => {
  switch (fileType) {
    case "image":
      return ImageIcon;
    case "pdf":
      return PictureAsPdfIcon;
    case "doc":
      return ArticleIcon;
    case "video":
      return VideoCameraBackIcon;
    case ProjectFileType.FOLDER:
      return FolderRounded;
    case "pinned":
      return FolderOpenIcon;
    case "trash":
      return DeleteIcon;
    default:
      return ArticleIcon;
  }
};

interface CustomTreeItemProps
  extends Omit<UseTreeItem2Parameters, "rootRef">,
    Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
  props: CustomTreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const { id, itemId, label, disabled, children, ...other } = props;

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getCheckboxProps,
    getLabelProps,
    getGroupTransitionProps,
    getDragAndDropOverlayProps,
    status,
    publicAPI,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

  // @ts-ignore
  const item = publicAPI.getItem(itemId);
  const expandable = isExpandable(children);
  let icon;
  if (expandable) {
    icon = FolderRounded;
  } else if (item.type) {
    icon = getIconFromFileType(item.type);
  }

  return (
    // @ts-ignore
    <TreeItem2Provider itemId={itemId}>
      <StyledTreeItemRoot {...getRootProps(other)}>
        <CustomTreeItemContent
          {...getContentProps({
            className: clsx("content", {
              "Mui-expanded": status.expanded,
              "Mui-selected": status.selected,
              "Mui-focused": status.focused,
              "Mui-disabled": status.disabled,
            }),
          })}
        >
          <TreeItem2IconContainer {...getIconContainerProps()}>
            <TreeItem2Icon status={status} />
          </TreeItem2IconContainer>
          <TreeItem2Checkbox {...getCheckboxProps()} />
          <CustomLabel {...getLabelProps({ icon, expandable: expandable && status.expanded })} />
          <TreeItem2DragAndDropOverlay {...getDragAndDropOverlayProps()} />
        </CustomTreeItemContent>
        {children && <TransitionComponent {...getGroupTransitionProps()} />}
      </StyledTreeItemRoot>
    </TreeItem2Provider>
  );
});

export default function Tree({
  data,
  submitFn,
  folderView,
  enableSelectFile = false,
  hideAddFile = false,
  allowMultiSelect = false,
  selectionMode = "default",
  showSubmitButton = folderView,
  submitLabel = "Select",
  mode = "browse",
}: {
  data: ProjectFile[];
  submitFn?: (selectedItems: string | string[]) => void;
  folderView?: boolean;
  enableSelectFile?: boolean;
  hideAddFile?: boolean;
  allowMultiSelect?: boolean;
  selectionMode?: "default" | "folder";
  showSubmitButton?: boolean;
  submitLabel?: string;
  mode?: "browse" | "folder-select" | "file-attach";
}) {
  const [query, setQuery] = React.useState("");
  const { searchResult, search } = useFileSearch(data);
  const { sortedData, sortBy, sortDirection, changeSort } = useSortProjectFile(
    searchResult.length > 0 ? searchResult : data
  );
  const { selectedItems, setSelectedItems } = React.useContext(ProjectFilesContext);
  const isFolderSelectionMode = selectionMode === "folder";
  const getItemLabel = React.useCallback((item: ProjectFile) => item.name, [data]);
  const isItemDisabled = React.useCallback(
    (item: ProjectFile) => {
      if (selectionMode === "folder") {
        return item.type !== ProjectFileType.FOLDER;
      }

      if (enableSelectFile) {
        return item.type !== ProjectFileType.FOLDER;
      }

      if (folderView && !enableSelectFile) {
        return item.type === ProjectFileType.FILE;
      }
    },
    [enableSelectFile, folderView, selectionMode]
  );

  const handleSelectedItemsChange = (event: React.SyntheticEvent, itemIds: Array<string>) => {
    if (enableSelectFile) {
      const nextSelectedItem = itemIds[itemIds.length - 1] ?? null;
      setSelectedItems(nextSelectedItem);
      return;
    }

    if (allowMultiSelect) {
      setSelectedItems(validateFileSelection(itemIds, data));
    } else {
      setSelectedItems(itemIds);
    }
  };

  const apiRef = useTreeViewApiRef();
  const handleClearSelection = (event) => {
    // @ts-ignore
    apiRef.current?.selectItem({ event, itemId: selectedItems, shouldBeSelected: false });
    setSelectedItems(null);
  };

  const handleSortChange = (column) => {
    const direction =
      sortBy === column && sortDirection === SortDirection.ASC
        ? SortDirection.DESC
        : SortDirection.ASC;
    changeSort(column, direction);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    search(value);
  };

  const findItemById = React.useCallback(
    (items: ProjectFile[], id?: string | null): ProjectFile | null => {
      if (!id) return null;

      for (const item of items) {
        if (item.id === id) {
          return item;
        }

        const children = item.children ?? [];
        const found = findItemById(children as ProjectFile[], id);
        if (found) {
          return found;
        }
      }

      return null;
    },
    []
  );

  const selectedItemId = Array.isArray(selectedItems) ? selectedItems[0] : selectedItems;
  const selectedFolder = React.useMemo(() => {
    const item = findItemById(data, selectedItemId as string | null);
    if (item?.type !== ProjectFileType.FOLDER) {
      return null;
    }

    return item;
  }, [data, findItemById, selectedItemId]);

  const shouldShowFolderFooter = !hideAddFile;
  const shouldShowSelectionFooter = showSubmitButton && mode === "folder-select";

  return (
    <MDBox
      sx={{ width: "100%", height: "100%", minHeight: 0, overflow: "hidden" }}
      style={{ backgroundColor: "white", borderRadius: 6 }}
      display="flex"
      flexDirection="column"
    >
      {data && (
        <>
          <MDBox
            textAlign="right"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="nowrap"
            gap={1}
            px={3}
            pb={hideAddFile ? 2 : 0}
            sx={{ flexShrink: 0 }}
          >
            <SearchAndSortFiles
              query={query}
              sortBy={sortBy}
              sortDirection={sortDirection}
              handleInputChange={handleInputChange}
              handleSortChange={handleSortChange}
            />
            {/* <Tooltip title="Clear Selection">
                <IconButton onClick={handleClearSelection}>
                  <Icon>clear</Icon>
                </IconButton>
              </Tooltip> */}
          </MDBox>

          <MDBox
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              pb: 1,
              maxHeight: "100%",
            }}
          >
            <RichTreeView
              items={sortedData}
              defaultExpandedItems={["1", "1.1"]}
              defaultSelectedItems={["1.1"]}
              itemChildrenIndentation={24}
              sx={{
                pr: 1,
                "& .MuiTreeItem-root": {
                  overflow: "hidden",
                },
              }}
              slots={{ item: CustomTreeItem }}
              getItemLabel={getItemLabel}
              isItemDisabled={isItemDisabled}
              apiRef={apiRef}
              multiSelect={allowMultiSelect}
              selectedItems={selectedItems}
              onSelectedItemsChange={handleSelectedItemsChange}
            />
          </MDBox>
        </>
      )}
      {shouldShowFolderFooter && (
        <MDBox
          p={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          flexWrap="nowrap"
          sx={{
            flexShrink: 0,
            bgcolor: "#fff",
            borderTop: "1px solid #eef1f5",
            zIndex: 2,
          }}
        >
          <AddFileOrFolder
            folderView
            lastSelectedItem={(selectedItems as string) ?? ""}
            fileById={{
              id: (selectedItems as string) ?? "",
              type: ProjectFileType.FOLDER,
              name: selectedFolder?.name ?? "",
              children: selectedFolder?.children ?? [],
            }}
            showUpload={false}
            showDeleteFolder
            onFolderDeleted={() => setSelectedItems(null)}
            trailingActions={
              shouldShowSelectionFooter ? (
                <MDButton
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={!selectedItems}
                  onClick={() => submitFn(selectedItems)}
                  sx={{
                    whiteSpace: "nowrap",
                    px: 2,
                    py: 0.75,
                    minHeight: "32px",
                    minWidth: "132px",
                  }}
                >
                  Select folder
                </MDButton>
              ) : null
            }
          />
        </MDBox>
      )}
    </MDBox>
  );
}
